import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { DemoModeBadge } from '@/components/ui/demo-mode-badge';
import { 
  ClipboardCheck, 
  ArrowLeft, 
  ArrowRight, 
  Save, 
  CheckCircle2,
  AlertCircle,
  X,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AuditType, AuditPriority, AuditStatus, AuditScope } from '@/types/asset-audit';
import { auditDataService } from '@/services/audit-data-service';
import { ProductTour } from '@/components/ui/product-tour';
import { useTour } from '@/contexts/TourContext';
import { useCreateAuditTour } from '@/hooks/use-create-audit-tour';
import { useAuditPermissions } from '@/hooks/use-permissions';

// Import wizard steps
import { AuditDetailsStep } from '@/components/audits/wizard/AuditDetailsStep';
import { DefineScopeStep } from '@/components/audits/wizard/DefineScopeStep';
import { ScheduleStep } from '@/components/audits/wizard/ScheduleStep';
import { AssignTeamStep } from '@/components/audits/wizard/AssignTeamStep';
import { ReviewStep } from '@/components/audits/wizard/ReviewStep';

export interface AuditFormData {
  // Step 1: Audit Details
  title: string;
  name?: string; // Alias for title used in some components
  description: string;
  type: AuditType;
  priority: AuditPriority;
  templateId?: string;
  scope?: string; // Scope description
  
  // Step 2: Define Scope
  scopeType: 'all' | 'location' | 'department' | 'category' | 'custom';
  locationIds: string[];
  departmentIds: string[];
  categoryIds: string[];
  assetIds: string[];
  
  // Step 3: Schedule
  scheduleType: 'immediate' | 'later' | 'recurring';
  startDate?: Date;
  dueDate?: Date;
  isRecurring?: boolean; // Whether the audit is recurring
  recurrencePattern?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  recurringFrequency?: string; // Alias for recurrencePattern
  recurrenceInterval?: number;
  recurrenceEndDate?: Date;
  recurringEndDate?: Date; // Alias for recurrenceEndDate
  
  // Step 4: Assign Team
  assigneeIds: string[];
  selectedTeamIds?: string[]; // Track which teams were selected
  notifyAssignees: boolean;
  notifyOnCompletion: boolean;
  allowReassignment: boolean;
}

const STEPS = [
  { id: 1, name: 'Audit Details', description: 'Basic information' },
  { id: 2, name: 'Define Scope', description: 'Select assets' },
  { id: 3, name: 'Schedule', description: 'Set timeline' },
  { id: 4, name: 'Assign Team', description: 'Add auditors' },
  { id: 5, name: 'Review', description: 'Confirm & submit' },
];

const STORAGE_KEY = 'audit_wizard_draft';

// Helper function to validate step 2 (Define Scope) - extracted to reduce cognitive complexity (S3776)
function isDefineScopeStepValid(formData: AuditFormData): boolean {
  if (formData.scopeType === 'all') return true;
  if (formData.scopeType === 'location') return formData.locationIds.length > 0;
  if (formData.scopeType === 'department') return formData.departmentIds.length > 0;
  if (formData.scopeType === 'category') return formData.categoryIds.length > 0;
  if (formData.scopeType === 'custom') return formData.assetIds.length > 0;
  return false;
}

// Helper function to validate step 3 (Schedule) - extracted to reduce cognitive complexity (S3776)
function isScheduleStepValid(formData: AuditFormData): boolean {
  if (formData.scheduleType === 'immediate') return true;
  if (formData.scheduleType === 'later') return !!(formData.startDate && formData.dueDate);
  if (formData.scheduleType === 'recurring') {
    return !!(formData.startDate && formData.dueDate && formData.recurrencePattern);
  }
  return false;
}

export default function CreateAudit() {
  const navigate = useNavigate();
  const { modules } = useFeatureSettings();
  const auditsModule = modules?.audits;
  const { canCreateAudits } = useAuditPermissions();

  if (!canCreateAudits) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">You do not have permission to create audits.</p>
      </div>
    );
  }
  
  // Product Tour
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useCreateAuditTour();
  const { setOnStartTour } = useTour();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [_isSubmitting, setIsSubmitting] = useState(false);
  const [showDraftRecovery, setShowDraftRecovery] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  
  const [formData, setFormData] = useState<AuditFormData>({
    title: '',
    description: '',
    type: AuditType.Physical,
    priority: AuditPriority.Medium,
    scopeType: 'all',
    locationIds: [],
    departmentIds: [],
    categoryIds: [],
    assetIds: [],
    scheduleType: 'immediate',
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Default to 7 days from now
    assigneeIds: [],
    selectedTeamIds: [],
    notifyAssignees: true,
    notifyOnCompletion: true,
    allowReassignment: false,
  });

  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => {
      setOnStartTour(null);
    };
  }, [setOnStartTour, startTour]);

  // Check for draft on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem(STORAGE_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        // Check if draft is less than 24 hours old
        const draftAge = Date.now() - draft.timestamp;
        if (draftAge < 24 * 60 * 60 * 1000) {
          setShowDraftRecovery(true);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error('Failed to parse draft:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = formData.title !== '' || 
                       formData.description !== '' || 
                       formData.locationIds.length > 0 ||
                       formData.departmentIds.length > 0 ||
                       formData.categoryIds.length > 0 ||
                       formData.assetIds.length > 0 ||
                       formData.assigneeIds.length > 0;
    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  // Auto-save draft
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.title || formData.description) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          data: formData,
          step: currentStep,
          timestamp: Date.now(),
        }));
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData, currentStep]);

  const handleRecoverDraft = () => {
    const savedDraft = localStorage.getItem(STORAGE_KEY);
    if (savedDraft) {
      const draft = JSON.parse(savedDraft);
      setFormData(draft.data);
      setCurrentStep(draft.step);
    }
    setShowDraftRecovery(false);
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShowDraftRecovery(false);
  };

  const updateFormData = (updates: Partial<AuditFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Use extracted helper functions to reduce cognitive complexity (S3776)
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1: // Audit Details
        return !!(formData.title && formData.type && formData.priority);
      case 2: // Define Scope
        return isDefineScopeStepValid(formData);
      case 3: // Schedule
        return isScheduleStepValid(formData);
      case 4: // Assign Team
        return formData.assigneeIds.length > 0;
      case 5: // Review
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length && isStepValid(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleStepClick = (stepId: number) => {
    // Allow jumping to previous steps or current step
    if (stepId <= currentStep) {
      setCurrentStep(stepId);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Helper function to convert date to ISO string
      const toISOString = (date: Date | string | undefined, fallback: Date): string => {
        if (!date) return fallback.toISOString();
        if (typeof date === 'string') return new Date(date).toISOString();
        return date.toISOString();
      };

      // Create the audit
      const newAudit = await auditDataService.createAudit({
        title: formData.title,
        description: formData.description,
        type: formData.type,
        status: formData.scheduleType === 'immediate' ? AuditStatus.Assigned : AuditStatus.Scheduled,
        priority: formData.priority,
        scope: formData.scope as AuditScope | undefined,
        scopeDetails: {
          locationIds: formData.locationIds,
          departmentIds: formData.departmentIds,
          assetIds: formData.assetIds,
        },
        scheduledStartDate: toISOString(formData.startDate, new Date()),
        scheduledEndDate: toISOString(formData.dueDate, new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
        assignedUsers: formData.assigneeIds.map(id => ({
          userId: id,
          userName: 'User ' + id,
          userEmail: `user${id}@example.com`,
          assignedDate: new Date().toISOString(),
          status: 'Pending' as any,
          completedAssets: 0,
          notificationSent: false,
        })),
        totalAssets: formData.assetIds.length,
        notifyAssignees: formData.notifyAssignees,
        notifyOnCompletion: formData.notifyOnCompletion,
        allowReassignment: formData.allowReassignment,
      } as any);

      // Clear draft
      localStorage.removeItem(STORAGE_KEY);

      // Navigate to the new audit
      navigate(`/audits/${newAudit.id}`);
    } catch (error) {
      console.error('Failed to create audit:', error);
      alert('Failed to create audit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowCloseDialog(true);
    } else {
      navigate(-1); // Go back to previous page
    }
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      // Save draft to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        data: formData,
        step: currentStep,
        timestamp: Date.now(),
      }));
      setLastSavedTime(new Date());
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSaveAndClose = async () => {
    await handleSaveDraft();
    setShowCloseDialog(false);
    navigate(-1);
  };

  const handleCloseWithoutSaving = () => {
    setShowCloseDialog(false);
    navigate(-1);
  };

  const formatLastSaved = (date: Date | null) => {
    if (!date) return '';
    
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const hours = date.getHours();
    const minutes = date.getMinutes();
    
    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinal = (n: number) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    
    // Format time as 12-hour with am/pm
    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    const minuteStr = minutes.toString().padStart(2, '0');
    
    return `${dayName} ${getOrdinal(day)} ${month} ${hour12}:${minuteStr}${period}`;
  };

  const progressPercentage = (currentStep / STEPS.length) * 100;

  return (
    <div className="flex h-full flex-col">
      {/* Draft Recovery Modal */}
      {showDraftRecovery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 border border-gray-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                <CardTitle>Draft Found</CardTitle>
              </div>
              <CardDescription>
                We found a saved draft from your previous session. Would you like to continue where you left off?
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2">
              <Button variant="outline" onClick={handleDiscardDraft} className="flex-1">
                Start Fresh
              </Button>
              <Button onClick={handleRecoverDraft} className="flex-1">
                Recover Draft
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="border-b bg-card p-6" data-tour="audit-wizard-header">
        <div className="px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="h-8 w-8 text-primary" />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold tracking-tight">Create New Audit</h1>
                  {auditsModule?.mode === 'demo' && <DemoModeBadge />}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground text-sm mt-0.5">
                  <span>
                    Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].name}
                  </span>
                  {lastSavedTime && (
                    <>
                      <span>•</span>
                      <span>
                        Last Modified: {formatLastSaved(lastSavedTime)}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || !hasUnsavedChanges}
                data-tour="audit-save-draft"
              >
                {isSavingDraft ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Draft
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={handleClose}>
                <X className="h-4 w-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-background">
        <div className="py-6 px-8">
          <div className="flex gap-6">
            {/* Vertical Steps Sidebar */}
            <div className="w-64 flex-shrink-0">
              <div className="sticky top-6 space-y-4">
              <Card className="border border-gray-200" data-tour="audit-steps-sidebar">
                <CardContent className="p-4">
                  <div className="space-y-1">
                    {STEPS.map((step, index) => (
                      <button
                        key={step.id}
                        onClick={() => handleStepClick(step.id)}
                        disabled={step.id > currentStep}
                        className={cn(
                          'w-full flex items-start gap-3 p-3 rounded-lg transition-all text-left',
                          step.id === currentStep && 'bg-primary/10',
                          step.id < currentStep && 'cursor-pointer hover:bg-muted',
                          step.id > currentStep && 'cursor-not-allowed opacity-50'
                        )}
                      >
                        <div
                          className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors flex-shrink-0',
                            step.id === currentStep && 'border-primary bg-primary text-primary-foreground',
                            step.id < currentStep && 'border-green-600 bg-green-600 text-white',
                            step.id > currentStep && 'border-muted-foreground/30'
                          )}
                        >
                          {step.id < currentStep ? (
                            <CheckCircle2 className="h-4 w-4" />
                          ) : (
                            <span className="text-sm font-medium">{step.id}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={cn(
                            "text-sm font-medium",
                            step.id === currentStep && 'text-primary',
                            step.id < currentStep && 'text-green-600',
                            step.id > currentStep && 'text-muted-foreground'
                          )}>
                            {step.name}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {step.description}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* Progress Indicator */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">Progress</span>
                      <span className="text-xs font-medium">{Math.round(progressPercentage)}%</span>
                    </div>
                    <Progress value={progressPercentage} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Navigation Buttons */}
              <div className="flex justify-between gap-3" data-tour="audit-navigation">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  size="lg"
                  className="flex-1"
                >
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={currentStep === STEPS.length ? handleSubmit : handleNext}
                  disabled={!isStepValid(currentStep)}
                  size="lg"
                  className="flex-1"
                >
                  {currentStep === STEPS.length ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Create Audit
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0 space-y-6" data-tour="audit-step-content">
              {/* Step Content */}
              {currentStep === 1 && (
                <AuditDetailsStep formData={formData} updateFormData={updateFormData} />
              )}
              {currentStep === 2 && (
                <DefineScopeStep formData={formData} updateFormData={updateFormData} />
              )}
              {currentStep === 3 && (
                <ScheduleStep formData={formData} updateFormData={updateFormData} />
              )}
              {currentStep === 4 && (
                <AssignTeamStep formData={formData} updateFormData={updateFormData} />
              )}
              {currentStep === 5 && (
                <ReviewStep formData={formData} updateFormData={updateFormData} onEdit={setCurrentStep} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Close Confirmation Dialog */}
      <AlertDialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save changes before leaving?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save them as a draft before closing?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseWithoutSaving}>
              Don't Save
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveAndClose}>
              Save Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Product Tour */}
      <ProductTour
        steps={tourSteps}
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={completeTour}
      />
    </div>
  );
}
